import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconConnector, IconPowertrain } from 'svgIcons/MotionPortalIcons';
import DroppableContainer from './DroppableContainer';
import SortableAssetItem from './SortableAssetItem';
import ValidationErrorBanner from './ValidationErrorBanner';
import { EMPTY_STATE_MESSAGES } from '../constants';
import { getAssetValidationStatus } from '../utils/validationUtils';

/**
 * PhysicalConnectionPanel - Panel displaying the physical connection sequence
 * Shows assets in their connected order with visual connection indicators
 * Includes validation error banner and asset validation states
 */
const PhysicalConnectionPanel = ({
    assets,
    activeId,
    title = 'Physical connection',
    validationResult = null,
    onOutputRatioChange = null,
    isDraggingConnectedAsset = false,
    isInvalidDropTarget = false,
    onAddGearboxClick = null,
    hasGearboxInConnection = false,
    onRemoveGearbox = null
}) => {
    const { t: translate } = useTranslation();
    const renderValidationBanner = () => {
        if (!validationResult || !validationResult?.hasErrors && !validationResult?.hasWarnings && validationResult?.globalErrors?.length === 0) {
            return null;
        }

        return (
            <ValidationErrorBanner
                validationResult={validationResult}
                className='physical-connection-banner'
            />
        );
    };

    const renderEmptyState = () =>
        <DroppableContainer id='physical-connection' className='empty-connection-area'>
            <div className='empty-content'>
                <div className='drag-icon'>
                    <IconPowertrain width={24} height={24} />
                </div>
                <span>
                    {EMPTY_STATE_MESSAGES.PHYSICAL_CONNECTION}
                </span>
            </div>
        </DroppableContainer>;

    const renderConnectionFlow = () =>
        <DroppableContainer id='physical-connection' className='connection-flow'>
            <SortableContext
                items={assets.map(asset => asset.id)}
                strategy={verticalListSortingStrategy}
            >
                {assets.map((asset, index) => {
                    const validationStatus = validationResult
                        ? getAssetValidationStatus(asset.assetId, validationResult)
                        : null;

                    return (
                        <React.Fragment key={asset.id}>
                            <div className='connection-slot'>
                                <SortableAssetItem
                                    asset={asset}
                                    isDragging={activeId === asset.id}
                                    validationStatus={validationStatus}
                                    onOutputRatioChange={onOutputRatioChange}
                                    isInSelectedAssets={false}
                                    onRemoveGearbox={onRemoveGearbox}
                                />
                            </div>
                            {index < assets.length - 1 &&
                                <div className='connection-link-vertical' aria-hidden='true'>
                                    <IconConnector width={20} height={20} />
                                </div>
                            }
                        </React.Fragment>
                    );
                })}
                {isDraggingConnectedAsset && assets.length > 0 &&
                    <div className='drop-not-allowed-indicator' />
                }
                {isInvalidDropTarget &&
                    <div className='invalid-drop-indicator' />
                }
            </SortableContext>
        </DroppableContainer>;

    return (
        <div className='physical-connection-panel'>
            <div className='panel-header'>
                <h3 className='panel-title'>{title}</h3>
                {onAddGearboxClick &&
                    <span className={hasGearboxInConnection ? 'add-gearbox-wrapper--disabled' : ''}>
                        <button
                            className={`add-gearbox-button${hasGearboxInConnection ? ' add-gearbox-button--disabled' : ''}`}
                            onClick={hasGearboxInConnection ? undefined : onAddGearboxClick}
                            type='button'
                            disabled={hasGearboxInConnection}
                            aria-disabled={hasGearboxInConnection}
                            title={hasGearboxInConnection
                                ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_AddGearboxDisabledTooltip')
                                : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_AddGearboxButton')
                            }
                        >
                            <svg
                                className='add-gearbox-icon'
                                width='12'
                                height='12'
                                viewBox='0 0 12 12'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                aria-hidden='true'
                            >
                                <path d='M12 5H7V0H5V5H0V7H5V12H7V7H12V5Z' fill='currentColor' />
                            </svg>
                            <span className='add-gearbox-label'>
                                {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_AddGearboxButton')}
                            </span>
                        </button>
                    </span>
                }
            </div>
            {renderValidationBanner()}
            <div className='connection-container'>
                {assets.length === 0 ? renderEmptyState() : renderConnectionFlow()}
            </div>
        </div>
    );
};

PhysicalConnectionPanel.propTypes = {
    assets: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        selectedSubType: PropTypes.string,
        outputSpeedRatio: PropTypes.string
    })).isRequired,
    activeId: PropTypes.string,
    isInvalidDropTarget: PropTypes.bool,
    title: PropTypes.string,
    validationResult: PropTypes.shape({
        isValid: PropTypes.bool.isRequired,
        hasErrors: PropTypes.bool.isRequired,
        hasWarnings: PropTypes.bool.isRequired,
        totalErrors: PropTypes.number.isRequired,
        totalWarnings: PropTypes.number.isRequired,
        globalErrors: PropTypes.array.isRequired,
        assetValidations: PropTypes.array.isRequired
    }),
    onOutputRatioChange: PropTypes.func,
    isDraggingConnectedAsset: PropTypes.bool,
    onAddGearboxClick: PropTypes.func,
    hasGearboxInConnection: PropTypes.bool,
    onRemoveGearbox: PropTypes.func
};

export default PhysicalConnectionPanel;
